/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useMemo, useRef } from 'react'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { FormDirtyIndicator } from '../components/form-dirty-indicator'
import { FormNavigationGuard } from '../components/form-navigation-guard'
import {
  SettingsForm,
  SettingsSwitchContent,
  SettingsSwitchItem,
} from '../components/settings-form-layout'
import { SettingsPageFormActions } from '../components/settings-page-context'
import { SettingsSection } from '../components/settings-section'
import { useUpdateOption } from '../hooks/use-update-option'

const oauthSchema = z.object({
  DingtalkOAuthEnabled: z.boolean(),
  DingtalkCorpId: z.string().optional(),
  DingtalkAppKey: z.string().optional(),
  DingtalkAppSecret: z.string().optional(),
  WeChatAuthEnabled: z.boolean(),
  WeChatServerAddress: z.string().optional(),
  WeChatServerToken: z.string().optional(),
  WeChatAccountQRCodeImageURL: z.string().optional(),
})

type OAuthFormValues = z.infer<typeof oauthSchema>

type OAuthSectionProps = {
  defaultValues: OAuthFormValues
}

export function OAuthSection({ defaultValues }: OAuthSectionProps) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()

  const normalizedDefaults = useMemo<OAuthFormValues>(
    () => ({
      ...defaultValues,
      DingtalkCorpId: defaultValues.DingtalkCorpId ?? '',
      DingtalkAppKey: defaultValues.DingtalkAppKey ?? '',
      DingtalkAppSecret: defaultValues.DingtalkAppSecret ?? '',
      WeChatServerAddress: defaultValues.WeChatServerAddress ?? '',
      WeChatServerToken: defaultValues.WeChatServerToken ?? '',
      WeChatAccountQRCodeImageURL:
        defaultValues.WeChatAccountQRCodeImageURL ?? '',
    }),
    [defaultValues]
  )

  const form = useForm<OAuthFormValues>({
    resolver: zodResolver(oauthSchema),
    defaultValues: normalizedDefaults,
  })

  const baselineRef = useRef<OAuthFormValues>(normalizedDefaults)
  const baselineSerializedRef = useRef<string>(
    JSON.stringify(normalizedDefaults)
  )

  useEffect(() => {
    const serialized = JSON.stringify(normalizedDefaults)
    if (serialized === baselineSerializedRef.current) return
    baselineRef.current = normalizedDefaults
    baselineSerializedRef.current = serialized
    form.reset(normalizedDefaults)
  }, [normalizedDefaults, form])

  const onSubmit = async () => {
    const rawData = form.getValues() as Record<string, unknown>

    const updates = Object.entries(rawData).filter(
      ([key, value]) =>
        value !== baselineRef.current[key as keyof OAuthFormValues]
    )

    if (updates.length === 0) {
      toast.info(t('No changes to save'))
      return
    }

    // Push enable toggles to the end. The backend rejects DingtalkOAuthEnabled=true
    // while DingtalkAppKey is still empty (controller/option.go), and the same
    // for WeChat. Saving credentials first guarantees the toggle's precondition.
    const enableKeys = new Set(['DingtalkOAuthEnabled', 'WeChatAuthEnabled'])
    const orderedUpdates = [
      ...updates.filter(([k]) => !enableKeys.has(k)),
      ...updates.filter(([k]) => enableKeys.has(k)),
    ]

    for (const [key, value] of orderedUpdates) {
      const safeValue: string | number | boolean =
        typeof value === 'boolean' || typeof value === 'number'
          ? value
          : typeof value === 'string'
            ? value
            : ''
      await updateOption.mutateAsync({ key, value: safeValue })
    }

    const savedValues = rawData as OAuthFormValues
    baselineRef.current = savedValues
    baselineSerializedRef.current = JSON.stringify(savedValues)
    form.reset(savedValues)
  }

  const handleReset = () => {
    form.reset(baselineRef.current, {
      keepDirty: false,
      keepDirtyValues: false,
      keepErrors: false,
    })
    toast.success(t('Form reset to saved values'))
  }

  return (
    <>
      <FormNavigationGuard when={form.formState.isDirty} />

      <SettingsSection
        title={t('OAuth Integrations')}
        description={t('Configure third-party authentication providers')}
      >
        <Form {...form}>
          <SettingsForm onSubmit={form.handleSubmit(onSubmit)}>
            <SettingsPageFormActions
              onSave={() => void form.handleSubmit(onSubmit)()}
              onReset={handleReset}
              isSaving={updateOption.isPending}
              isResetDisabled={!form.formState.isDirty}
            />
            <FormDirtyIndicator isDirty={form.formState.isDirty} />

            <FormField
              control={form.control}
              name='DingtalkOAuthEnabled'
              render={({ field }) => (
                <SettingsSwitchItem>
                  <SettingsSwitchContent>
                    <FormLabel>{t('Enable DingTalk Auth')}</FormLabel>
                    <FormDescription>
                      {t('Allow users to sign in with DingTalk')}
                    </FormDescription>
                  </SettingsSwitchContent>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </SettingsSwitchItem>
              )}
            />

            <FormField
              control={form.control}
              name='DingtalkCorpId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Corp ID')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('DingTalk Corp ID (optional)')}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('Restrict login to members of this organization')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='DingtalkAppKey'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('App Key')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('DingTalk App Key')}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='DingtalkAppSecret'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('App Secret')}</FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder={t('DingTalk App Secret')}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='WeChatAuthEnabled'
              render={({ field }) => (
                <SettingsSwitchItem>
                  <SettingsSwitchContent>
                    <FormLabel>{t('Enable WeChat Auth')}</FormLabel>
                    <FormDescription>
                      {t('Allow users to sign in with WeChat')}
                    </FormDescription>
                  </SettingsSwitchContent>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </SettingsSwitchItem>
              )}
            />

            <FormField
              control={form.control}
              name='WeChatServerAddress'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Server Address')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('https://wechat-server.example.com')}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='WeChatServerToken'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Server Token')}</FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder={t('Server Token')}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='WeChatAccountQRCodeImageURL'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('QR Code Image URL')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('https://example.com/qr-code.png')}
                      autoComplete='off'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </SettingsForm>
        </Form>
      </SettingsSection>
    </>
  )
}
